<script setup lang="ts">
import { computed, h, ref } from 'vue'

import { Plus } from '@lucide/vue'
import { useQuery } from '@tanstack/vue-query'
import {
  type ColumnDef,
  createSortedRowModel,
  rowSortingFeature,
  sortFn_alphanumeric,
  sortFn_datetime,
  tableFeatures,
  useTable,
} from '@tanstack/vue-table'
import { RouterLink } from 'vue-router'

import DataTable from '#shared/components/DataTable.vue'
import ListToolbar from '#shared/components/ListToolbar.vue'
import PageHeader from '#shared/components/PageHeader.vue'
import RelativeDate from '#shared/components/RelativeDate.vue'
import SearchInput from '#shared/components/SearchInput.vue'
import SortableHeader from '#shared/components/SortableHeader.vue'
import { contextHubClient } from '#shared/services/api/context-hub.client'
import type { components } from '#shared/services/api/context-hub.schema'
import { Badge } from '#ui/badge'
import { Button } from '#ui/button'
import { ICONS } from '#utils/icons'
import { ROW_CLASS, ROW_LINK_CLASS } from '#utils/table'

type SkillDto = components['schemas']['SkillDto']

definePage({
  meta: { requiresAuth: true, title: 'Skills' },
})

const search = ref('')

const skillsQuery = useQuery({
  queryKey: ['skills'] as const,
  queryFn: async (): Promise<SkillDto[]> => {
    const { data } = await contextHubClient.GET('/api/admin/skills')
    return data ?? []
  },
})

const features = tableFeatures({
  rowSortingFeature,
  sortedRowModel: createSortedRowModel(),
  sortFns: { alphanumeric: sortFn_alphanumeric, datetime: sortFn_datetime },
})

const columns: ColumnDef<typeof features, SkillDto>[] = [
  {
    accessorKey: 'name',
    header: ({ column }) =>
      h(SortableHeader, {
        label: 'Name',
        sorted: column.getIsSorted(),
        onToggle: column.getToggleSortingHandler(),
      }),
    cell: ({ row }) =>
      h(
        RouterLink,
        { to: `/skills/${row.original.id}`, class: `font-medium ${ROW_LINK_CLASS}` },
        () => row.original.name ?? '-',
      ),
  },
  {
    accessorKey: 'description',
    header: 'Description',
    cell: ({ row }) =>
      h(
        'span',
        { class: 'line-clamp-2 text-sm text-muted-foreground' },
        row.original.description ?? '',
      ),
  },
  {
    accessorKey: 'tags',
    header: 'Tags',
    cell: ({ row }) =>
      h(
        'span',
        { class: 'text-sm text-muted-foreground' },
        (row.original.tags ?? []).join(', ') || '—',
      ),
  },
  {
    accessorKey: 'isEnabled',
    header: 'Status',
    cell: ({ row }) =>
      h(Badge, { variant: row.original.isEnabled ? 'success' : 'secondary' }, () =>
        row.original.isEnabled ? 'Enabled' : 'Disabled',
      ),
  },
  {
    accessorKey: 'updatedAt',
    header: 'Updated',
    cell: ({ row }) => h(RelativeDate, { value: row.original.updatedAt }),
  },
]

const all = computed(() => skillsQuery.data.value ?? [])

const filtered = computed(() => {
  const q = search.value.trim().toLowerCase()
  if (!q) return all.value
  return all.value.filter((skill) =>
    [skill.name, skill.description, ...(skill.tags ?? [])]
      .filter(Boolean)
      .some((value) => String(value).toLowerCase().includes(q)),
  )
})

const table = useTable({
  features,
  data: filtered,
  columns,
})
</script>

<template>
  <PageHeader title="Skills">
    <template #actions>
      <Button as-child>
        <RouterLink to="/skills/new">
          <Plus />
          New skill
        </RouterLink>
      </Button>
    </template>
  </PageHeader>

  <div class="flex flex-col gap-3">
    <ListToolbar
      noun="skills"
      singular="skill"
      :shown="skillsQuery.isSuccess.value ? filtered.length : undefined"
      :total="all.length"
    >
      <SearchInput v-model="search" label="Search skills" placeholder="Search by name or tag…" />
    </ListToolbar>

    <DataTable
      :table="table"
      :is-loading="skillsQuery.isLoading.value"
      :error="skillsQuery.error.value"
      :filtered="!!search.trim()"
      noun="skills"
      :icon="ICONS.skills"
      :row-class="ROW_CLASS"
      @clear="search = ''"
    >
      <template #empty-description>
        A skill is a playbook the assistant can run inside a customer's conversation.
      </template>
      <template #empty-action>
        <Button as-child>
          <RouterLink to="/skills/new">
            <Plus />
            New skill
          </RouterLink>
        </Button>
      </template>
    </DataTable>
  </div>
</template>
